/**
 * Voor-na-vergelijking — sliderweergave
 *
 * Als custom element opgezet, zodat de theme editor de sectie opnieuw kan
 * renderen zonder dat de bediening stukgaat: een nieuw element upgradet
 * vanzelf. Geen externe libraries.
 *
 * --vnv-pos is de stand van de scheidslijn, gemeten vanaf links. Links
 * daarvan zie je het voor-beeld, rechts het na-beeld.
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* Drempel waarboven we de beweging als horizontaal slepen beschouwen.
   Daaronder laten we de browser zijn gang gaan, zodat verticaal scrollen
   op mobiel niet geblokkeerd raakt. */
const DRAG_THRESHOLD = 10;

/* Eenmalig per paginaweergave. Een module wordt één keer geëvalueerd, dus
   deze vlag geldt voor alle sliders op de pagina samen. */
let interactionTracked = false;

function trackFirstInteraction(el) {
  if (interactionTracked) return;
  interactionTracked = true;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'voor_na_interactie',
    lp: el.dataset.lp || 'trimmen-zonder-gedoe',
    weergave: 'slider'
  });
}

class VoorNaSlider extends HTMLElement {
  connectedCallback() {
    this.frame = this.querySelector('[data-vnv-frame]');
    this.handle = this.querySelector('[data-vnv-handle]');
    if (!this.frame || !this.handle) return;

    this.position = this.clamp(Number(this.dataset.start || 50));
    this.dragging = false;
    this.candidate = false;
    this.startX = 0;
    this.startY = 0;
    this.pointerId = null;

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.frame.addEventListener('pointerdown', this.onPointerDown);
    this.frame.addEventListener('pointermove', this.onPointerMove);
    this.frame.addEventListener('pointerup', this.onPointerUp);
    this.frame.addEventListener('pointercancel', this.onPointerUp);
    this.handle.addEventListener('keydown', this.onKeyDown);

    this.render();
  }

  disconnectedCallback() {
    if (!this.frame) return;
    this.frame.removeEventListener('pointerdown', this.onPointerDown);
    this.frame.removeEventListener('pointermove', this.onPointerMove);
    this.frame.removeEventListener('pointerup', this.onPointerUp);
    this.frame.removeEventListener('pointercancel', this.onPointerUp);
    this.handle.removeEventListener('keydown', this.onKeyDown);
  }

  clamp(value) {
    if (Number.isNaN(value)) return 50;
    return Math.min(100, Math.max(0, value));
  }

  /**
   * @param {number}  value    Nieuwe stand in procenten.
   * @param {boolean} byUser   True als de bezoeker hem zelf verplaatst.
   */
  setPosition(value, byUser) {
    const next = this.clamp(value);
    if (next === this.position && !byUser) return;

    this.position = next;
    this.render();

    if (byUser) trackFirstInteraction(this);
  }

  render() {
    const rounded = Math.round(this.position);
    this.style.setProperty('--vnv-pos', `${this.position}%`);
    this.handle.setAttribute('aria-valuenow', String(rounded));
    // De scheidslijn staat op deze stand; links ervan zie je het voor-beeld.
    this.handle.setAttribute(
      'aria-valuetext',
      `${rounded}% van het voor-beeld zichtbaar`
    );
  }

  positionFromClientX(clientX) {
    const rect = this.frame.getBoundingClientRect();
    if (!rect.width) return this.position;
    return ((clientX - rect.left) / rect.width) * 100;
  }

  onPointerDown(event) {
    // Nog niet slepen en nog niet vastpakken: eerst afwachten welke kant
    // de beweging op gaat, anders stelen we het verticale scrollen.
    this.candidate = true;
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startY = event.clientY;
  }

  onPointerMove(event) {
    if (this.dragging) {
      event.preventDefault();
      this.setPosition(this.positionFromClientX(event.clientX), true);
      return;
    }

    if (!this.candidate || event.pointerId !== this.pointerId) return;

    const dx = event.clientX - this.startX;
    const dy = event.clientY - this.startY;

    if (Math.abs(dx) >= DRAG_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
      this.dragging = true;
      this.classList.add('is-dragging');
      if (this.frame.setPointerCapture) {
        this.frame.setPointerCapture(event.pointerId);
      }
      this.setPosition(this.positionFromClientX(event.clientX), true);
    } else if (Math.abs(dy) >= DRAG_THRESHOLD) {
      // Overwegend verticaal: laat los, dit is een scrollbeweging.
      this.candidate = false;
    }
  }

  onPointerUp(event) {
    if (this.dragging && this.frame.releasePointerCapture) {
      try {
        this.frame.releasePointerCapture(event.pointerId);
      } catch (e) {
        /* pointer was al losgelaten */
      }
    }
    this.dragging = false;
    this.candidate = false;
    this.pointerId = null;
    this.classList.remove('is-dragging');
  }

  onKeyDown(event) {
    const step = event.shiftKey ? 10 : 2;
    let next = null;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        next = this.position - step;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        next = this.position + step;
        break;
      case 'PageDown':
        next = this.position - 10;
        break;
      case 'PageUp':
        next = this.position + 10;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = 100;
        break;
      default:
        return;
    }

    event.preventDefault();

    if (REDUCED) {
      // Geen animatie op de handgreep: zet hem in één keer neer.
      this.classList.add('is-dragging');
      this.setPosition(next, true);
      this.classList.remove('is-dragging');
      return;
    }

    this.setPosition(next, true);
  }
}

if (!customElements.get('voor-na-slider')) {
  customElements.define('voor-na-slider', VoorNaSlider);
}
